import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { FaInstagram, FaFacebookF, FaLinkedinIn } from "react-icons/fa";
import { NAV_LINKS, SOCIALS } from "@/data/content";
import { PillButton } from "@/components/PillButton";
import { scrollToId } from "@/lib/scroll";
import { useScrollSpy } from "@/hooks/useScrollSpy";
import { useScrollLock } from "@/hooks/useScrollLock";

const Logo = () => (
  <span className="grid place-items-center h-10 w-10 rounded-xl bg-[image:var(--gradient-cta)] text-[#0C0C0C] shadow-[var(--shadow-warm)]">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
    </svg>
  </span>
);

const listVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] } },
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const active = useScrollSpy(NAV_LINKS.map((l) => l.id));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useScrollLock(open);

  const go = (id) => { setOpen(false); setTimeout(() => scrollToId(id), 30); };

  return (
    <>
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled
          ? "bg-[rgba(12,12,12,0.72)] backdrop-blur-xl border-b border-[var(--border-subtle)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="container-pad flex items-center justify-between h-[68px] md:h-[76px]">
        <button data-testid="navbar-logo" onClick={() => go("home")} className="flex items-center gap-3 focus-ring rounded-lg">
          <Logo />
          <span className="text-left leading-none">
            <span className="block font-playfair font-bold text-[var(--text-primary)] tracking-[0.05em] text-lg md:text-xl">GODAVARI</span>
            <span className="block font-inter text-[8px] md:text-[10px] tracking-[0.32em] text-[var(--text-muted)] uppercase mt-1">Builders &amp; Developers</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-1 rounded-full border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.04)] backdrop-blur-md p-1.5">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              data-testid={`navbar-link-${link.id}`}
              aria-current={active === link.id ? "true" : undefined}
              onClick={() => go(link.id)}
              className={`px-4 py-2 rounded-full text-sm font-inter transition-colors duration-200 ${
                active === link.id
                  ? "bg-[image:var(--gradient-cta)] text-[#0C0C0C] font-semibold"
                  : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <PillButton data-testid="navbar-primary-cta-button" onClick={() => go("contact")} className="hidden sm:inline-flex">
            Book Now
          </PillButton>
          <button
            data-testid="navbar-open-menu-button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="lg:hidden h-11 w-11 inline-flex items-center justify-center rounded-full border border-[var(--border-subtle)] bg-[rgba(255,245,230,0.04)] text-[var(--text-primary)] focus-ring"
          >
            <Menu size={20} />
          </button>
        </div>
      </nav>
    </header>

    {/* Mobile / tablet menu — rendered OUTSIDE the header so the header's
        backdrop-blur doesn't become a containing block that traps this
        position:fixed overlay (which was hiding it after any scroll). */}
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[90] flex flex-col overflow-y-auto overscroll-contain bg-[rgba(12,12,12,0.96)] backdrop-blur-2xl lg:hidden"
          data-lenis-prevent
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="container-pad flex items-center justify-between h-[68px] shrink-0">
            <span className="flex items-center gap-3"><Logo /><span className="font-playfair font-bold tracking-[0.05em] text-lg">GODAVARI</span></span>
            <button data-testid="navbar-close-menu-button" aria-label="Close menu" onClick={() => setOpen(false)} className="h-11 w-11 grid place-items-center rounded-full border border-[var(--border-subtle)] text-[var(--text-primary)] focus-ring">
              <X size={20} />
            </button>
          </div>
          <motion.ul variants={listVariants} initial="hidden" animate="show" className="container-pad mt-4 flex flex-col gap-1 sm:mt-6">
            {NAV_LINKS.map((link) => (
              <motion.li variants={itemVariants} key={link.id}>
                <button onClick={() => go(link.id)} className={`w-full text-left py-3 font-playfair text-3xl transition-colors sm:text-4xl ${active === link.id ? "text-[var(--accent-secondary)]" : "text-[var(--text-primary)] hover:text-[var(--accent-secondary)]"}`}>
                  {link.label}
                </button>
              </motion.li>
            ))}
          </motion.ul>
          <div className="container-pad mt-auto mb-10 flex flex-col gap-5 pt-8">
            <PillButton onClick={() => go("contact")} className="w-full">Book Now</PillButton>
            <div className="flex items-center gap-3">
              {[{ I: FaInstagram, h: SOCIALS.instagram }, { I: FaLinkedinIn, h: SOCIALS.linkedinCompany }, { I: FaFacebookF, h: SOCIALS.facebook }].map(({ I, h }, i) => (
                <a key={i} href={h} target="_blank" rel="noopener noreferrer" className="h-11 w-11 grid place-items-center rounded-full border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--accent-secondary)] hover:border-[var(--border-hover)] transition-colors">
                  <I size={16} />
                </a>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};
